import { useRef, useState } from "react";

import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Snackbar,
    Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";

import { uploadJobFile } from "../../api/fileRoutes";
import {ALLOWED_TYPES, MAX_FILE_SIZE} from "../../constants/constants";

export default function UploadFile({
                                       job,
                                       onUploadSuccess
                                   }) {
    const inputRef = useRef(null);

    const [loading, setLoading] = useState(false);

    const [error, setError] = useState("");

    const handleBrowse = () => {
        inputRef.current?.click();
    };

    const validateFile = (file) => {
        if (!file) {
            return "Please select a file.";
        }

        if (!ALLOWED_TYPES.includes(file.type)) {
            return "Only PDF or image files are allowed.";
        }

        if (file.size > MAX_FILE_SIZE) {
            return "File size must be less than 20MB.";
        }

        return null;
    };

    const handleFileChange = async (event) => {
        const file = event.target.files?.[0];

        const validationError = validateFile(file);

        if (validationError) {
            setError(validationError);

            return;
        }

        try {
            setLoading(true);
            setError("");

            const response = await uploadJobFile(
                job.id,
                file
            );

            console.log(response);

            if (onUploadSuccess) {
                onUploadSuccess();
            }

        } catch (err) {
            console.error(err);

            if (
                err.response?.data?.detail
            ) {
                setError(err.response.data.detail);
            } else if (
                err.message === "Network Error"
            ) {
                setError(
                    "Network error. Please check your connection."
                );
            } else {
                setError(
                    "Something went wrong while uploading the file."
                );
            }
        } finally {
            setLoading(false);

            event.target.value = "";
        }
    };

    return (
        <>
            <Box
                sx={{
                    flex: 1,
                    p: 3,
                    overflow: "auto",
                }}
            >
                <Box
                    sx={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        borderRadius: 5,
                        overflow: "hidden",
                        border: "1px solid #e4e4e7",
                        backgroundColor: "#fafafa",
                        backgroundImage:
                            "radial-gradient(rgba(0,0,0,.12) 1px, transparent 1px)",
                        backgroundSize:
                            "16px 16px",
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent:
                                "center",
                        }}
                    >
                        <Card
                            sx={{
                                width: 360,
                                borderRadius: 5,
                                border:
                                    "1px solid #e5e7eb",
                                boxShadow:
                                    "0 10px 40px rgba(0,0,0,.06)",
                            }}
                        >
                            <CardContent
                                sx={{
                                    py: 6,
                                    px: 5,
                                    textAlign:
                                        "center",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 72,
                                        height: 72,
                                        borderRadius:
                                            "50%",
                                        display:
                                            "flex",
                                        alignItems:
                                            "center",
                                        justifyContent:
                                            "center",
                                        margin:
                                            "0 auto",
                                        background:
                                            "linear-gradient(135deg,#ede9fe 0%,#f3e8ff 100%)",
                                    }}
                                >
                                    <CloudUploadOutlinedIcon
                                        sx={{
                                            fontSize: 34,
                                            color: "#7c3aed",
                                        }}
                                    />
                                </Box>

                                <Typography
                                    sx={{
                                        mt: 3,
                                        fontWeight: 700,
                                        fontSize: 18,
                                    }}
                                >
                                    Upload document
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        mt: 1.5,
                                        lineHeight:
                                            1.9,
                                    }}
                                >
                                    Supported formats:
                                    PDF, PNG, JPG,
                                    TIFF
                                </Typography>

                                <Button
                                    variant="contained"
                                    disabled={
                                        loading
                                    }
                                    onClick={
                                        handleBrowse
                                    }
                                    sx={{
                                        mt: 4,
                                        textTransform:
                                            "none",
                                        borderRadius: 3,
                                        px: 4,
                                        boxShadow: 0,
                                        background:
                                            "linear-gradient(135deg,#7c3aed 0%, #a855f7 100%)",
                                    }}
                                >
                                    {loading ? (
                                        <CircularProgress
                                            size={20}
                                            color="inherit"
                                        />
                                    ) : (
                                        "Browse file"
                                    )}
                                </Button>

                                <input
                                    ref={inputRef}
                                    type="file"
                                    hidden
                                    accept=".pdf,image/*"
                                    onChange={
                                        handleFileChange
                                    }
                                />
                            </CardContent>
                        </Card>
                    </Box>
                </Box>
            </Box>

            <Snackbar
                open={!!error}
                autoHideDuration={4000}
                onClose={() =>
                    setError("")
                }
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert
                    severity="error"
                    variant="filled"
                    onClose={() =>
                        setError("")
                    }
                >
                    {error}
                </Alert>
            </Snackbar>
        </>
    );
}

UploadFile.propTypes = {
    job: PropTypes.object.isRequired,
    onUploadSuccess: PropTypes.func.isRequired,
};